import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    ReportDraft,
    ReportDraftItem,
    ReportDraftTextBlock
} from '../../types/reportDrafts';
import { reportDraftsApi } from '../../api/reportDraftsApi';
import {
    ReportDraftRenderModel,
    buildReportDraftRenderModel
} from '../../utils/reportDraftView';

export type ReportDraftSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface UseReportDraftReturn {
    draft: ReportDraft | null;
    /** Render model built from the draft snapshot (editor + PDF input). */
    renderModel: ReportDraftRenderModel | null;
    loading: boolean;
    error: string | null;
    saveStatus: ReportDraftSaveStatus;
    /** Message for saveStatus 'error' (e.g. "Utkastet har raderats"). */
    saveError: string | null;
    /** Immediate write: include/exclude, photo toggles, hidden fields. */
    updateItem: (eventId: string, item: ReportDraftItem) => void;
    /** Debounced write for typed edits (comments, text overrides, captions). */
    updateItemDebounced: (eventId: string, item: ReportDraftItem) => void;
    /** Create or update a text block; text edits should pass debounce=true. */
    saveTextBlock: (block: Omit<ReportDraftTextBlock, 'key'> & { key?: string }, debounce?: boolean) => void;
    removeTextBlock: (blockKey: string) => void;
    updateReportTitleDebounced: (reportTitle: string) => void;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds between retries
const DEBOUNCE_DELAY = 1000; // 1 second, like useDraftPersistence
const SAVED_BADGE_DELAY = 2000; // how long the "Sparat" state lingers

/**
 * Editor hook for one report draft (plan D6/D14): loads the self-contained
 * draft snapshot (one Firebase node — the live timeline is never fetched) and
 * autosaves curation edits with optimistic local state.
 *
 * Writes are field-granular (reportDraftsApi atomic updates), so two admins
 * editing different items rarely collide; last write wins by design (non-goal:
 * co-editing). Failed writes retry 3× before surfacing saveStatus 'error'.
 */
export const useReportDraft = (draftKey: string | undefined): UseReportDraftReturn => {
    const [draft, setDraft] = useState<ReportDraft | null>(null);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [draftError, setDraftError] = useState<string | null>(null);
    const [saveStatus, setSaveStatus] = useState<ReportDraftSaveStatus>('idle');
    const [saveError, setSaveError] = useState<string | null>(null);

    // Debounce timers per logical field, pending write count for saveStatus
    const debounceTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    const inflightWritesRef = useRef(0);
    const savedBadgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (!draftKey) return;
            setLoadingDraft(true);
            setDraftError(null);
            try {
                const loaded = await reportDraftsApi.getDraft(draftKey);
                if (cancelled) return;
                if (!loaded) {
                    setDraftError('Utkastet hittades inte');
                } else {
                    setDraft(loaded);
                }
            } catch (err) {
                console.error('Error loading report draft:', err);
                if (!cancelled) setDraftError('Kunde inte ladda utkastet');
            } finally {
                if (!cancelled) setLoadingDraft(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [draftKey]);

    // Clear pending debounce timers on unmount. Deliberately NOT flushing:
    // in-flight typed edits within the last second are lost on hard navigation,
    // same trade-off as useDraftPersistence.
    useEffect(() => {
        const timers = debounceTimersRef.current;
        return () => {
            timers.forEach(t => clearTimeout(t));
            timers.clear();
            if (savedBadgeTimerRef.current) clearTimeout(savedBadgeTimerRef.current);
        };
    }, []);

    /**
     * Run a write with retry + saveStatus bookkeeping. A write awaiting retry
     * still counts as in-flight, so "Sparat" never shows while data is unsaved
     * (F6). Only Firebase errors (carrying a .code) are retried — validation
     * throws from the API layer surface immediately.
     */
    const performWrite = useCallback((write: () => Promise<void>, retryCount = 0) => {
        if (retryCount === 0) {
            inflightWritesRef.current += 1;
        }
        setSaveStatus('saving');
        if (savedBadgeTimerRef.current) {
            clearTimeout(savedBadgeTimerRef.current);
            savedBadgeTimerRef.current = null;
        }

        write()
            .then(() => {
                inflightWritesRef.current -= 1;
                setSaveError(null);
                if (inflightWritesRef.current === 0) {
                    setSaveStatus('saved');
                    savedBadgeTimerRef.current = setTimeout(() => setSaveStatus('idle'), SAVED_BADGE_DELAY);
                }
            })
            .catch(err => {
                console.error(`Error saving report draft (attempt ${retryCount + 1}/${MAX_RETRIES}):`, err);
                const retryable = typeof (err as { code?: unknown })?.code === 'string';
                if (retryable && retryCount < MAX_RETRIES - 1) {
                    setTimeout(() => performWrite(write, retryCount + 1), RETRY_DELAY);
                } else {
                    inflightWritesRef.current -= 1;
                    setSaveError(err instanceof Error ? err.message : null);
                    setSaveStatus('error');
                }
            });
    }, []);

    /**
     * Cancel a pending debounced save for a key. Immediate writes of the same
     * subtree must call this first: the immediate payload is built from the
     * optimistic state and already contains the pending typed changes, so the
     * stale timer would otherwise fire later and revert them (F1).
     */
    const cancelDebounced = useCallback((debounceKey: string) => {
        const timers = debounceTimersRef.current;
        const pending = timers.get(debounceKey);
        if (pending) {
            clearTimeout(pending);
            timers.delete(debounceKey);
        }
    }, []);

    /** Debounce a write under a logical key (one timer per item/block/field). */
    const performWriteDebounced = useCallback((debounceKey: string, write: () => Promise<void>) => {
        const timers = debounceTimersRef.current;
        const existing = timers.get(debounceKey);
        if (existing) clearTimeout(existing);
        timers.set(debounceKey, setTimeout(() => {
            timers.delete(debounceKey);
            performWrite(write);
        }, DEBOUNCE_DELAY));
    }, [performWrite]);

    const updateItem = useCallback((eventId: string, item: ReportDraftItem) => {
        if (!draftKey) return;
        cancelDebounced(`item:${eventId}`);
        setDraft(prev => prev ? { ...prev, items: { ...prev.items, [eventId]: item } } : prev);
        performWrite(() => reportDraftsApi.setItem(draftKey, eventId, item));
    }, [draftKey, performWrite, cancelDebounced]);

    const updateItemDebounced = useCallback((eventId: string, item: ReportDraftItem) => {
        if (!draftKey) return;
        setDraft(prev => prev ? { ...prev, items: { ...prev.items, [eventId]: item } } : prev);
        performWriteDebounced(`item:${eventId}`, () => reportDraftsApi.setItem(draftKey, eventId, item));
    }, [draftKey, performWriteDebounced]);

    const saveTextBlock = useCallback((block: Omit<ReportDraftTextBlock, 'key'> & { key?: string }, debounce = false) => {
        if (!draftKey) return;
        // Mint the Firebase auto-ID up front so optimistic state and the
        // eventual write agree (the API accepts a pre-set key).
        const key = block.key || reportDraftsApi.newTextBlockKey(draftKey);
        const saved: ReportDraftTextBlock = { ...block, key };
        setDraft(prev => prev ? { ...prev, textBlocks: { ...prev.textBlocks, [key]: saved } } : prev);
        const write = () => reportDraftsApi.setTextBlock(draftKey, saved);
        if (debounce) {
            performWriteDebounced(`block:${key}`, write);
        } else {
            cancelDebounced(`block:${key}`);
            performWrite(write);
        }
    }, [draftKey, performWrite, performWriteDebounced, cancelDebounced]);

    const removeTextBlock = useCallback((blockKey: string) => {
        if (!draftKey) return;
        cancelDebounced(`block:${blockKey}`);
        setDraft(prev => {
            if (!prev) return prev;
            const textBlocks = { ...prev.textBlocks };
            delete textBlocks[blockKey];
            return { ...prev, textBlocks };
        });
        performWrite(() => reportDraftsApi.removeTextBlock(draftKey, blockKey));
    }, [draftKey, performWrite, cancelDebounced]);

    const updateReportTitleDebounced = useCallback((reportTitle: string) => {
        if (!draftKey) return;
        setDraft(prev => prev ? { ...prev, reportTitle } : prev);
        performWriteDebounced('meta:reportTitle', () => reportDraftsApi.updateDraftMeta(draftKey, { reportTitle }));
    }, [draftKey, performWriteDebounced]);

    const renderModel = useMemo(() =>
        draft ? buildReportDraftRenderModel(draft) : null,
        [draft]
    );

    return {
        draft,
        renderModel,
        loading: loadingDraft,
        error: draftError,
        saveStatus,
        saveError,
        updateItem,
        updateItemDebounced,
        saveTextBlock,
        removeTextBlock,
        updateReportTitleDebounced,
    };
};
